<script setup lang="ts">
import type { ITableColumnsOptions } from '@modules/tables/types';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import { useAffiliateChangeStatus } from '@modules/affiliates-pending/composables';
import {
  AFFILIATES_REQUEST_HISTORY_PERMISSIONS,
  AFFILIATES_REQUEST_HISTORY_PROFILE_ROUTE_NAME,
  TRANSLATION_GROUP_AFFILIATES_REQUEST_HISTORY,
} from '@modules/affiliates-request-history/constants';
import {
  useDownloadAffiliatesRequestHistoryReportMutation,
} from '@modules/affiliates-request-history/mutations';
import { useAffiliatesRequestHistoryListQuery } from '@modules/affiliates-request-history/queries';
import { EAasmState } from '@modules/core/enums';
import VDownloadReportButton
  from '@modules/download-report/components/VDownloadReportButton/VDownloadReportButton.vue';
import { useDownloadReport } from '@modules/download-report/composables';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useAffiliatesRequestHistoryListQuery);

const columnOptions: ITableColumnsOptions = {
  aasm_state: {
    localization: {
      translationPath: 'admin.partners.request_history.aasm_state.',
    },
  },
  email: {
    defaultMobilePreview: true,
  },
};

const {
  isReportLoading,
  onDownloadReport,
} = useDownloadReport(useDownloadAffiliatesRequestHistoryReportMutation(), tableState);

const { reapprove } = useAffiliateChangeStatus();
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliatesRequestHistoryListPage'" type="table">
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="$getTPath(TRANSLATION_GROUP_AFFILIATES_REQUEST_HISTORY, 'HEADER')"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="listItem.aasm_state === EAasmState.DENIED && $hasPermission(AFFILIATES_REQUEST_HISTORY_PERMISSIONS.CHANGE_STATUS)"
          icon="pi-check"
          severity="success"
          @click="reapprove(listItem.id, $event)"
        />
        <VIconButton
          icon="pi-id-card"
          :to="{
            name: AFFILIATES_REQUEST_HISTORY_PROFILE_ROUTE_NAME,
            params: {
              id: listItem.id,
              tab: 'general',
            },
          }"
        />
      </template>
      <template
        v-if="$hasPermission(AFFILIATES_REQUEST_HISTORY_PERMISSIONS.DOWNLOAD_LIST)"
        #footerButtons
      >
        <VDownloadReportButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_REQUEST_HISTORY, 'DOWNLOAD_REPORT')"
          :loading="isReportLoading"
          @download="onDownloadReport"
        />
      </template>
    </VTable>
  </VCard>
</template>
